import { compilerCiceroToFronto } from "../cicero-fronto/compile.js";
import { loadCiceroFromSpec } from "../cicero/cicero.js";
import { isEmptyRule, genCallID, getCallTime } from "./callQueue.js";
import { renderFronto } from "../fronto/fronto.js";
import { viewSpecs, storeUID, storeViewSpec, unlockNew, unlockSpec, callQueue, unlockedSpecsMemory } from "./storage.js";
import { deepcopy } from "./utility.js";
import { get } from "svelte/store";

let FirstRule = {
  category: "manual",
  description: "Make a new artboard",
  specifier: {
    role: "artboard"
  },
  action: "modify",
  option: {
  }
};
let SecondRule = {
  category: "manual",
  description: "Set width & height",
  specifier: {
    role: "view"
  },
  action: "modify",
  option: {
  }
};
let ThirdRule = {
  category: "manual",
  description: "Rescale map",
  specifier: {
    role: "view"
  },
  action: "modify",
  option: {
    projection: {}
  }
}
const projectionDefaultWidth = {
  mercator: 630,
  transverseMercator: 495,
  albers: 540,
  albersUSA: 90,
  azimuthalEqualArea: 400,
  azimuthalEquidistant: 520,
  conicConformal: 720,
  conicEqualArea: 630,
  conicEquidistant: 735,
  equalEarth: 500,
  equirectangular: 640,
  gnomonic: 350,
  identity: 1,
  orthographic: 200,
  stereographic: 590
}
const LATEST = "Latest"

function versionCompare(a, b) {
  if (a.length == 0 && b.length == 0) return true;
  return a?.[a.length - 1]?.callID === b?.[b.length - 1]?.callID;
}

export class ViewSpec {
  constructor(uid, artboard, history, config) {
    this.fronto = {}; // auto-gen
    this.original_fronto = config?.fronto || {};
    this.viewObj; // auto-gen
    this.history = history || [];
    this.is_view_only = config.view_only || false;
    this.artboard = artboard || {}; // artboard design
    this.original_artboard = config?.original_artboard || artboard || {}; // for safety
    this.uid = uid;
    this.domId = "artboard-" + uid;
    this.selector = "#artboard-" + uid;
    this.is_first = config.is_first || false;
    this.source = config.source || undefined;
    this.versions = [];
    this.currVersion = config?.currVersion;
    this.n_branches = config?.n_branches || 0;
    this.last_updated = getCallTime();
    if (config?.versions) {
      this.versions = config?.versions || [];
      if (config?.branched) {
        this.changeAfterCheckout = false;
      } else {
        let version = this.versions.filter(v => v.name === config?.currVersion)?.[0] || this.versions[this.versions.length - 1];
        if (!versionCompare(this.history, version?.save)) {
          this.changeAfterCheckout = true;
          this.saveVersion();
        }
      }
    } else if (config?.fronto) {
      this.changeAfterCheckout = true;
      this.saveVersion();
    }
    if (config.fromCache) {
      this.checkoutLatestVersion();
    } else if (this.currVersion) this.checkoutVersion(this.currVersion);
    else this.checkoutLatestVersion();
    this.changeAfterCheckout = false;
  }

  async render(_data, _datatype, afterRender, _hypo, hypo_additional) {
    let isHypo = _hypo && _hypo.constructor.name === "Array"; // Cicero Transformations;
    if (_data) this.data = _data;
    let data = this.data;
    if (_datatype) {
      this.datatype = _datatype.stats;
    }
    let datatype = this.datatype;
    let transformations = this.history.filter(r => !isEmptyRule(r) && r.specifier.role && !r.specifier.role?.startsWith("artboard"));
    if (isHypo && hypo_additional) {
      transformations.push(..._hypo);
    } else if (isHypo) {
      transformations = _hypo;
    }
    let artboardUpdates = this.history.filter(r => !isEmptyRule(r) && r.specifier.role?.startsWith("artboard"));
    this.artboard = await compileArtboardUpdates(this.artboard, artboardUpdates);
    let artboard = this.artboard;
    let selector = this.selector;
    let fronto = this.original_fronto;
    fronto.data = data;
    fronto.datatype = datatype;
    let self = this;
    let metadata = {};
    let name = this.uid;
    let description = `This is ${isHypo ? "hypothetical " : ""}Artboard ${name}.`;
    let ciceroObj = await loadCiceroFromSpec(
      { name, description, metadata, transformations },
      fronto,
      compilerCiceroToFronto,
      renderFronto
    )
    if (document.querySelector(selector)) document.querySelector(selector).innerHTML = "";
    let transformed = await ciceroObj.getRendered(selector, { baseUrl: artboard.baseUrl });
    self.fronto = transformed.compiled;
    transformed.rendered.then((result, err) => {
      if (err) console.error(err);
      self.viewObj = result.viewObject;
      self.compiledView = result.compiled;
      if (!isHypo) self.last_updated = getCallTime();
      if (afterRender) afterRender();
    });
  }

  async hypothesize(_data, _datatype, _hypo, afterRender) {
    return await this.render(_data, _datatype, afterRender, _hypo);
  }

  async hypothesize_additional(_data, _datatype, _hypo, afterRender) {
    return await this.render(_data, _datatype, afterRender, _hypo, true);
  }

  async dehypothesize(_data, _datatype, afterRender) {
    return await this.render(_data, _datatype, afterRender);
  }

  getManualEdits() {
    return this.history.filter((rule) => isNonArtboardRule(rule) && isCustomRule(rule));
  }

  updateHistory(rules, config) {
    if (config?.replace) this.history = [];
    this.history.push(...rules);
    this.changeAfterCheckout = true;
    return this;
  }

  storeToCache() {
    if (!this.is_view_only) {
      let uid = this.uid;
      let artboard = this.artboard;
      let history = this.history;
      let original_artboard = this.original_artboard;
      let fronto = this.original_fronto;
      // register uid to cache;
      let uids = localStorage.getItem("dupo-uids");
      let is_first = this.is_first || false;
      let source = this.source;
      let versions = this.versions;
      let currVersion = this.currVersion;
      let n_branches = this.n_branches;
      if (!uids) uids = [];
      else uids = JSON.parse(uids);
      if (!uids.includes(uid)) {
        uids.push(uid);
        localStorage.setItem("dupo-uids", JSON.stringify(uids));
      }
      // update the spec (local)
      storeViewSpec(uid, { uid, artboard, history, original_artboard, fronto, is_first, source, versions, currVersion, n_branches });
    }
  }

  saveVersion(rec) {
    if (this.changeAfterCheckout) {
      let save = deepcopy(this.history);
      let name = `Version ${this.versions.length + 1}`;
      let version = { name, save };
      version.fromRecommendation = rec;
      this.versions.push(version);
      this.changeAfterCheckout = false;
      this.storeToCache();
      return name;
    }
  }

  addVersion(_save, fromRecommendation) {
    if (_save && callQueue) {
      let save = _save.map((rule) => {
        rule.callID = genCallID(get(callQueue).usedCallIDs);
        rule.callTime = getCallTime();
        return rule;
      })
      let first = this.history[0];
      if (first.description === FirstRule.description) {
        save.splice(0, 0, deepcopy(first));
      }
      let name = `Version ${this.versions.length + 1}`;
      let version = { name, save, fromRecommendation };
      this.versions.push(version);
      let selfUid = this.uid;
      callQueue.update((queue) => {
        queue.add_as_processed(save, [selfUid]);
        return queue;
      });
      this.storeToCache();
      return name;
    }
  }

  checkoutVersion(name) {
    if (this.changeAfterCheckout) {
      this.saveVersion();
      this.changeAfterCheckout = false;
    }
    let v = this.versions.filter((v) => v.name === name)[0];
    if (v) {
      this.history = deepcopy(v.save);
      this.currVersion = v.name;
      this.changeAfterCheckout = false;
    }
    this.storeToCache();
    return this;
  }

  checkoutLatestVersion() {
    let name = this.versions?.[this.versions.length - 1]?.name;
    if (name) this.checkoutVersion(name)
    this.storeToCache();
    return this;
  }

  async discardLastRecVersion() {
    let indices = this.versions.map((v, i) => v.fromRecommendation ? i : undefined).filter((d) => d !== undefined);
    let index = Math.max(...indices);
    if (index !== undefined) {
      console.log(this.versions.length, this.versions[index]);
      this.last_deleted_version = this.versions.splice(index, 1)[0];
      if (this.last_deleted_version.name === this.currVersion) {
        this.last_deleted_version.save = deepcopy(this.history);
      }
      console.log(this.versions.length, this.versions[index]);
      this.last_deleted_version_index = index;
      this.storeToCache();
      this.checkoutLatestVersion();
    }
    return this;
  }

  async revertLastDiscardedVersion() {
    if (this.last_deleted_version && this.last_deleted_version_index !== undefined) {
      let index = this.last_deleted_version_index;
      let version = this.last_deleted_version;
      let index2;
      this.versions.forEach((v, i) => {
        if (v.name === version.name) index2 = i;
      });
      console.log(this.versions.length, this.versions[index]);
      if (index2 !== undefined) {
        this.versions[index2] = version;
      } else {
        this.versions.splice(index, 0, version);
      }
      console.log(this.versions.length, this.versions[index]);
      // force checkout to a version
      this.history = deepcopy(version.save);
      this.currVersion = version.name;
      this.changeAfterCheckout = false;
      this.storeToCache();
    }
    return this;
  }

  addVersionWithCheckout(save, callQueue, rec) {
    if (save && callQueue) {
      this.saveVersion();
      let uid = this.uid;
      callQueue.update((queue) => {
        queue.add(save, [uid]).apply({
          afterApply: (rules, spec) => {
            spec.history = rules;
            let vname = spec.saveVersion(rec);
            spec.changeAfterCheckout = true;
            spec.checkoutVersion(vname);
            spec.changeAfterCheckout = false;
          }
        });
        return queue;
      });
    }
    return this;
  }

  async duplicate(newArtboard) {
    let uid = genSpecUid();
    let artboard = newArtboard || deepcopy(this.artboard);
    if (!newArtboard) artboard.name += "-dup";
    let fronto = this.history.length == 0 ? deepcopy(this.original_fronto) : deepcopy(this.fronto);
    let original_artboard = deepcopy(this.original_artboard);
    let _first = deepcopy(FirstRule);
    _first.option = newArtboard;
    _first.isOriginal = true;
    _first.callID = genCallID(get(callQueue).usedCallIDs);
    _first.callTime = getCallTime();
    let _second = deepcopy(SecondRule);
    _second.option = { width: newArtboard.artboard_width, height: newArtboard.artboard_height };
    _second.isOriginal = true;
    _second.callID = genCallID(get(callQueue).usedCallIDs);
    _second.callTime = getCallTime();
    let history = [_first, _second];
    if (fronto.layout?.composition === "projection") {
      let _third = deepcopy(ThirdRule);
      let old_scale = fronto.layout.projection.scale;
      let old_scale_rate = fronto.layout?.width / projectionDefaultWidth[fronto.layout.projection?.type || "mercator"];
      let factor = old_scale / old_scale_rate;
      let new_scale = _second.option.width / projectionDefaultWidth[fronto.layout.projection?.type || "mercator"];
      let new_scale_rate = new_scale * factor;
      _third.option.projection.scale = new_scale_rate;
      _third.isOriginal = true;
      _third.callID = genCallID(get(callQueue).usedCallIDs);
      _third.callTime = getCallTime();
      history.push(_third);
    }
    let is_first = false;
    let source;
    // let sameScreen = artboard.screen_type.split("-")[0] === this.artboard.screen_type.split("-")[0];
    // if (!sameScreen)
    if (this.source) {
      source = this.source;
    } else {
      source = this.uid;
    }

    storeUID(uid);
    viewSpecs.update(specs => {
      let nb = new ViewSpec(uid, artboard, history, { original_artboard, fronto, is_first, source });
      specs.push(nb);
      nb.storeToCache();
      return specs;
    });
    if (get(unlockNew)) {
      unlockSpec(uid);
    }
    return uid;
  }

  async branchOut(rules, fromRecommendation, newArtboard) {
    let this_uid = this.uid;
    let uid = genSpecUid();
    // artboard
    let artboard = {};
    let n_branches = this.n_branches;
    Object.keys(this.artboard).forEach((key) => {
      if (newArtboard?.[key]) artboard[key] = newArtboard[key];
      else {
        if (key === "name") {
          artboard.name = (this.artboard.name + "-branch-" + (n_branches));
        } else {
          artboard[key] = this.artboard[key];
        }
      }
    });

    // first rule & version
    let first = deepcopy(this.history[0]);
    first.option = artboard;
    let save = rules?.map((rule) => {
      rule.callID = genCallID(get(callQueue).usedCallIDs);
      rule.callTime = getCallTime();
      return rule;
    }) || [];
    if (first.description === FirstRule.description && first.description === save[0].description) {
      save[0] = first;
    } else {
      save.splice(0, 0, first);
    }
    let name = "Version 1";
    let version = { name, save, fromRecommendation };

    // config
    let fronto = deepcopy(this.original_fronto);
    let original_artboard = deepcopy(this.original_artboard);
    original_artboard.name = artboard.name;
    let config = {
      fronto,
      versions: [version],
      original_artboard,
      is_first: false,
      currVersion: name,
      branched: true
    };
    if (this.source) config.source = this.source;

    // store
    storeUID(uid);
    viewSpecs.update(specs => {
      let nb = new ViewSpec(uid, artboard, [], config);
      let oi = specs.findIndex((spec) => spec.uid == this_uid);
      if (oi >= 0) specs.splice(oi + 1, 0, nb);
      else specs.push(nb);
      nb.storeToCache();
      return specs;
    });
    callQueue.update((queue) => {
      queue.add_as_processed(save, [uid]);
      return queue;
    });
    if (get(unlockNew)) {
      if (!fromRecommendation) unlockSpec(uid);
      else unlockedSpecsMemory.update((uids) => {
        uids.push(uid);
        return uids;
      })
    }
    this.n_branches += 1;
    return uid;
  }
}

export function makeFromCache(data, view_only) {
  let config = {}, history = [];
  if (data.original_artboard) config.original_artboard = data.original_artboard;
  if (data.fronto) {
    config.fronto = data.fronto;
  }
  if (data.versions) {
    config.versions = data.versions;
  }
  if (data.currVersion) {
    config.currVersion = data.currVersion;
  }
  if (data.n_branches) {
    config.n_branches = data.n_branches;
  }
  config.is_first = data.is_first || false;
  config.source = data.source || undefined;
  config.view_only = view_only || false;
  history = data.history;
  config.fromCache = true;
  return new ViewSpec(data.uid, data.artboard, history, config);
}

const availLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'.split('');
const nLetters = availLetters.length;
const rsLength = 6;
export function genSpecUid() {
  let r = new Array(rsLength).fill(0).map(d => availLetters[Math.floor(Math.random() * nLetters)]).join('');
  return "view_" + r;
}

async function compileArtboardUpdates(artboard, rules) {
  for (const rule of rules) {
    if (rule.specifier?.role == "artboard") {
      if (rule.action === "modify") {
        Object.keys(rule?.option)?.forEach(key => {
          artboard[key] = rule.option[key]
        });
      }
    }
  }
  return artboard;
}

export function isNonArtboardRule(rule) {
  return rule.specifier?.role !== "artboard";
}

export function isManualRule(rule) {
  return rule.category === "manual" || rule.category === undefined;
}

export function isCustomRule(rule) {
  return rule.category !== "recommender";
}

export function isRecommenderRule(rule) {
  return rule.category === "recommender";
}